import { StructuredTextGraphQlResponse, renderMetaTags, useQuerySubscription } from "react-datocms";
import { TitleMetaLinkTag } from "react-datocms";
import { GetStaticProps } from "next";
import { Api, IPlanObject } from "@/api";
import { Layout, SEO } from "@/components";
import { PAGE_RECORDS, DATOCMS_API_TOKEN, EXTERNAL } from "@/config";
import { getLiveSelectPlans } from "@/helpers";
import {
  metaTagsFragment,
  getLinkFieldAndFragments,
  Modules,
  MODULE_KEYS,
  ModuleToComponent,
  addPlanDataToModules,
} from "@/modules";
import { SiteBanner } from "@/modules/site-banner";
import { Subscription, IPage, ImageType } from "@/types";
import { createUrl } from "@/utils";
import { AnchorButton, PromoBanner } from "@narrative-software/narrative-web-ui";
import { responsiveImageFragment } from "../../modules/config";

type PricingCMSPage = {
  title: string;
  subheading: string;
  parentPage: IPage;
  modules: Modules;
  seo: TitleMetaLinkTag[];
  planBackground: ImageType;
};

type PageContent = {
  pages: PricingCMSPage[];
  banner: {
    enabled: boolean;
    content: StructuredTextGraphQlResponse;
  };
};

type PricingPageProps = {
  plans: IPlanObject[];
  preview: boolean;
  subscription: Subscription<PageContent>;
};

const PricingPage = ({ plans, preview, subscription }: PricingPageProps) => {
  const { data: { pages, banner } = {} } = useQuerySubscription(subscription);

  const data = pages?.find((page): page is PricingCMSPage => page.parentPage.slug === "select");

  if (!data) return null;

  const { title, subheading, modules, seo } = data;

  const modulesWithPlanData = addPlanDataToModules(
    // TODO: Remove this filter once the testimonial carousel is removed from the cms page post launch
    // This is to allow us to accurately review the page in the staging environments
    modules.filter((m) => m._modelApiKey !== "module_testimonial_carousel"),
    plans,
  );

  return (
    <Layout product="select" preview={preview} siteBanner={banner ? <SiteBanner {...banner} /> : null}>
      <SEO>{renderMetaTags(seo)}</SEO>
      <PromoBanner
        title={<span className="text-h1">{title}</span>}
        description={subheading}
        textColour="white"
        backgroundImagePath="/images/textures/purple-bg-alt@2x.png"
      >
        <AnchorButton href={createUrl(EXTERNAL.SELECT.GET_STARTED.HREF)} colour="white" className="text-sm normal-case">
          Get Select Free
        </AnchorButton>
      </PromoBanner>
      <div>
        <ModuleToComponent modules={modulesWithPlanData} addBackgroundColour={false} />
      </div>
    </Layout>
  );
};

/**
 * Static props
 */
export const getStaticProps: GetStaticProps<PricingPageProps> = async ({ preview = false }) => {
  // Get plans
  const response = await Api.getPlans({ "filter[product-name]": "Select" });
  const plans = getLiveSelectPlans(response.data.data);

  const [modulesField, modulesFragments] = getLinkFieldAndFragments([
    MODULE_KEYS.carousel,
    MODULE_KEYS.timeCostCalculator,
    MODULE_KEYS.accordion,
    MODULE_KEYS.selectPlanComparison,
    MODULE_KEYS.lifetimeDealModule,
  ]);

  // Get DatoCMS content
  const graphqlRequest = {
    preview,
    query: `
      {
        pages: allPagePricings {
          title
          subheading
          planBackground {
            responsiveImage(imgixParams: { auto: format, w: 495, h: 517 }) {
              ...responsiveImageFragment
            }
          }
          parentPage {
            ... on ${PAGE_RECORDS.select} {
              slug
            }
          }
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
          ${modulesField}
        }
        banner: moduleSiteBanner {
          enabled
          content {
            value
          }
        }
      }
      ${responsiveImageFragment}
      ${modulesFragments}
      ${metaTagsFragment}
    `,
  };

  const {
    data: { data: initialData },
  } = await Api.datocmsGraphqlRequest<PageContent>(graphqlRequest);

  return {
    props: {
      plans,
      preview,
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData,
            token: DATOCMS_API_TOKEN,
          }
        : {
            initialData,
            enabled: false,
          },
    },
  };
};

export default PricingPage;
